import React, { useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GetAppOutlinedIcon from "@mui/icons-material/GetAppOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { usePWA } from "../hooks/usePWA";
import { useOfflineSync } from "../providers/OfflineSyncProvider";
import { usePushNotifications } from "../push/PushNotificationProvider";
import { APP_NAME, APP_TAGLINE } from "../config/brand";

/**
 * App / PWA settings — install help, display mode, and "Installed as App" layout toggle.
 * Linked from the account menu on all dashboard roles.
 */
export default function AppSettingsPage() {
  const location = useLocation();
  const profilePath = location.pathname.replace(/\/app-settings\/?$/, "/profile");

  const {
    isStandalone,
    isInstalledAsApp,
    isIOS,
    isAndroid,
    isSecure,
    canInstall,
    installInstructions,
    preferAppLayout,
    setPreferAppLayout,
    promptInstall,
    resetInstallHints,
  } = usePWA();
  const { pendingCount, runSync, syncing } = useOfflineSync();
  const { supported: pushSupported, configured: pushConfigured, permission: pushPermission, subscribe: subscribePush, error: pushError } =
    usePushNotifications();

  const [installMessage, setInstallMessage] = useState("");

  const handleAndroidInstall = async () => {
    const { outcome } = await promptInstall();
    if (outcome === "accepted") {
      setInstallMessage("Installing — check your home screen.");
    } else if (outcome === "dismissed") {
      setInstallMessage("Install cancelled. You can try again anytime.");
    } else {
      setInstallMessage("Install is not available in this browser yet. Use Chrome and visit over HTTPS.");
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, md: 3 } }}>
      <Button
        component={RouterLink}
        to={profilePath}
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2 }}
      >
        Back to profile
      </Button>

      <Typography variant="h5" fontWeight={700} gutterBottom>
        App settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {APP_NAME} works in your browser and as an installed app — same features and data.
      </Typography>

      <Stack spacing={2.5}>
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Running mode
            </Typography>
            <Chip
              size="small"
              color={isInstalledAsApp ? "success" : "default"}
              icon={isInstalledAsApp ? <CheckCircleOutlineIcon /> : undefined}
              label={isInstalledAsApp ? "Installed app" : "Browser tab"}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {isInstalledAsApp
              ? "You opened Maphunziro from your home screen (standalone). The address bar is hidden for more screen space."
              : "You are using the normal website in a browser — tabs and the address bar work as usual."}
          </Typography>
        </Paper>

        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <FormControlLabel
            sx={{ alignItems: "flex-start", m: 0 }}
            control={
              <Switch
                checked={preferAppLayout && isStandalone}
                disabled={!isStandalone}
                onChange={(e) => setPreferAppLayout(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  Installed as App
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isStandalone
                    ? "Adjust layout for fullscreen (safe areas, compact chrome). Turn off if you prefer the classic spacing."
                    : "Available after you install and open from your home screen. Browser tabs keep the standard layout."}
                </Typography>
              </Box>
            }
          />
        </Paper>

        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Install {APP_NAME}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {APP_TAGLINE} — optional install, never required.
          </Typography>

          {!isSecure && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Install requires HTTPS. Use your production URL to add the app to your home screen.
            </Alert>
          )}

          {installMessage && (
            <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInstallMessage("")}>
              {installMessage}
            </Alert>
          )}

          {isAndroid && !isStandalone && (
            <Stack spacing={1.5}>
              <Alert severity="info" icon={<GetAppOutlinedIcon />}>
                On Android, tap Install when Chrome offers it. The app uses the same account as the website.
              </Alert>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                <Button
                  variant="contained"
                  startIcon={<GetAppOutlinedIcon />}
                  disabled={!canInstall}
                  onClick={handleAndroidInstall}
                >
                  Install app
                </Button>
                {!canInstall && (
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                    Open in Chrome over HTTPS; the button enables when install is offered.
                  </Typography>
                )}
              </Stack>
            </Stack>
          )}

          {isIOS && !isStandalone && (
            <Stack spacing={1} component="ol" sx={{ pl: 2.5, m: 0 }}>
              {installInstructions.steps.map((step) => (
                <Typography key={step} component="li" variant="body2" sx={{ mb: 0.5 }}>
                  {step}
                </Typography>
              ))}
            </Stack>
          )}

          {isStandalone && (
            <Alert severity="success">
              Already installed. Remove it from your device home screen settings if you want to use only the browser.
            </Alert>
          )}

          {!isAndroid && !isIOS && !isStandalone && (
            <Typography variant="body2" color="text.secondary">
              On desktop, use your browser menu (Install app / Create shortcut). On mobile, use Chrome (Android) or Safari (iOS).
            </Typography>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Offline & sync
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Attendance, marks, and notices are cached on this device for low-connectivity areas. Saved work uploads
            automatically when you are back online.
          </Typography>
          {pendingCount > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {pendingCount} item{pendingCount === 1 ? "" : "s"} waiting to upload.
            </Alert>
          )}
          <Button variant="outlined" disabled={syncing} onClick={() => runSync()}>
            Sync now
          </Button>
        </Paper>

        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Push notifications
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Get alerts for notices and submission approvals. Configure{" "}
            <code>REACT_APP_FCM_VAPID_KEY</code> or <code>REACT_APP_ONESIGNAL_APP_ID</code> to enable.
          </Typography>
          {pushError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {pushError}
            </Alert>
          )}
          <Button
            variant="outlined"
            disabled={!pushSupported || !pushConfigured || pushPermission === "granted"}
            onClick={() => subscribePush()}
          >
            {pushPermission === "granted" ? "Notifications enabled" : "Enable notifications"}
          </Button>
        </Paper>
      </Stack>

      <Divider sx={{ my: 3 }} />
      <Button size="small" onClick={resetInstallHints}>
        Reset dismissed install hints
      </Button>
    </Container>
  );
}
